
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import API from '../services/api';
import { maskPath } from '../services/urlMask';


const NotificationContext = createContext(null);

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const { user, authenticated } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);

    // Refs so callbacks don't need authenticated/user in their dep arrays,
    // preventing the cascade: authenticated changes → callbacks recreated → useEffect re-runs → WS reconnects.
    const authenticatedRef = useRef(authenticated);
    const userRef = useRef(user);
    useEffect(() => { authenticatedRef.current = authenticated; }, [authenticated]);
    useEffect(() => { userRef.current = user; }, [user]);

    const fetchNotifications = useCallback(async () => {
        if (!authenticatedRef.current) return;
        try {
            const response = await API.get('/notifications/?limit=20');
            setNotifications(response.data);

            const countResponse = await API.get('/notifications/unread-count');
            setUnreadCount(countResponse.data.count);
        } catch (error) {
            console.error("Failed to fetch notifications", error);
        }
    }, []); // stable — authenticatedRef keeps value current without recreating

    // WebSocket connection management
    const connectWebSocket = useCallback(() => {
        if (!authenticatedRef.current || !userRef.current) {
            console.log("Not authenticated, skipping WebSocket connection");
            return;
        }

        // Get token from localStorage
        const token = localStorage.getItem('token');
        if (!token) {
            console.log("No token found, skipping WebSocket connection");
            return;
        }

        // Close existing connection if any
        if (wsRef.current) {
            wsRef.current.close();
        }

        // Determine WebSocket URL
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = window.location.host;
        // Masked: the route and the ?token= JWT are folded into the opaque blob so they
        // don't appear in the Network tab / logs. The backend decodes it and (already)
        // authenticates the WebSocket via the session cookie, so behavior is unchanged.
        const wsUrl = `${protocol}//${host}/api${maskPath(`/ws/notifications?token=${token}`)}`;

        console.log("Connecting to WebSocket:", wsUrl);

        try {
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                console.log("WebSocket connected successfully");
                reconnectAttemptsRef.current = 0;
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    console.log("WebSocket message received:", data);

                    if (data.type === 'notification') {
                        // New notification received
                        const newNotification = data.data;

                        // Add to notifications list
                        setNotifications(prev => [newNotification, ...prev]);

                        // Increment unread count if not read
                        if (!newNotification.is_read) {
                            setUnreadCount(prev => prev + 1);
                        }

                        // Trigger toast popup for warnings and errors
                        if (newNotification.type === 'warning' || newNotification.type === 'error') {
                            window.dispatchEvent(new CustomEvent('show-notification-toast', { detail: newNotification }));
                        }
                    } else if (data.type === 'connected') {
                        console.log("WebSocket handshake complete:", data.message);
                    } else if (data.type === 'pong') {
                        // Heartbeat response
                    }
                } catch (error) {
                    console.error("Failed to parse WebSocket message:", error);
                }
            };

            ws.onerror = (error) => {
                console.error("WebSocket error:", error);
            };

            ws.onclose = (event) => {
                console.log("WebSocket closed:", event.code, event.reason);
                wsRef.current = null;

                // Attempt to reconnect with exponential backoff
                if (authenticatedRef.current) {
                    reconnectAttemptsRef.current += 1;
                    const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
                    console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);

                    reconnectTimeoutRef.current = setTimeout(() => {
                        connectWebSocket();
                    }, delay);
                }
            };

            // Heartbeat to keep connection alive
            const heartbeatInterval = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send('ping');
                }
            }, 30000); // Send ping every 30 seconds

            // Store interval ID for cleanup
            ws.heartbeatInterval = heartbeatInterval;

        } catch (error) {
            console.error("Failed to create WebSocket:", error);
        }
    }, []); // stable — refs keep authenticated/user current without recreating

    // Initial fetch and WebSocket setup
    useEffect(() => {
        if (!authenticated) {
            setNotifications([]);
            setUnreadCount(0);

            // Close WebSocket if exists
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }

            // Clear reconnect timeout
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }

            return;
        }

        // Fetch initial notifications
        fetchNotifications();

        // Connect WebSocket
        connectWebSocket();

        // Cleanup on unmount
        return () => {
            if (wsRef.current) {
                // Clear heartbeat
                if (wsRef.current.heartbeatInterval) {
                    clearInterval(wsRef.current.heartbeatInterval);
                }
                wsRef.current.close();
                wsRef.current = null;
            }

            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };
    }, [authenticated, fetchNotifications, connectWebSocket]);

    const markAsRead = async (id) => {
        try {
            await API.patch(`/notifications/${id}/read`);
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error("Failed to mark as read", error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await API.post(`/notifications/mark-all-read`);
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error("Failed to mark all as read", error);
        }
    };

    const clearAll = async () => {
        try {
            await API.delete(`/notifications/clear-all`);
            setNotifications([]);
            setUnreadCount(0);
        } catch (error) {
            console.error("Failed to clear notifications", error);
        }
    };

    return (
        <NotificationContext.Provider value={{
            notifications,
            unreadCount,
            markAsRead,
            markAllAsRead,
            clearAll,
            fetchNotifications
        }}>
            {children}
        </NotificationContext.Provider>
    );
};
